import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box, Typography, TextField, Button, Tab, Tabs, Paper, Alert, CircularProgress
} from '@mui/material';

import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import HowToRegIcon from '@mui/icons-material/HowToReg';

const API = process.env.REACT_APP_API_URL || '';

const fieldSx = { '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'var(--c-primario)' } } };
const labelStyle = { style: { color: 'var(--c-acento)' } };
const inputStyle = { style: { color: '#e0e0e0' } };

export default function Login({ onLogin }) {
  const [tab, setTab] = useState(0);
  const [form, setForm] = useState({ nombre: '', apellido: '', dni: '', email: '', password: '', telefono: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Configuración de pantalla de inicio
  const [inicio, setInicio] = useState({ nombre_clinica: '', subtitulo: '', logo_base64: '' });
  useEffect(() => {
    axios.get(`${API}/api/configuracion/inicio`)
      .then(r => setInicio(r.data))
      .catch(() => {});
  }, []);

  // Estado del DNI en registro
  const [verificandoDni, setVerificandoDni] = useState(false);
  const [estadoDni, setEstadoDni] = useState(null); // null | 'libre' | 'pre_registrado' | 'ocupado'
  const [nombrePreRegistrado, setNombrePreRegistrado] = useState('');

  // Recuperar contraseña
  const [recuperando, setRecuperando] = useState(false);
  const [formRec, setFormRec] = useState({ dni: '', email: '', nueva_password: '', confirmar: '' });
  const [exitoRec, setExitoRec] = useState('');

  function guardarSesion(data) {
    // sessionStorage: se borra automáticamente al cerrar el navegador/pestaña
    sessionStorage.setItem('token', data.token);
    sessionStorage.setItem('rol', data.rol);
    sessionStorage.setItem('nombre', data.nombre);
    sessionStorage.setItem('expira', String(Date.now() + 8 * 60 * 60 * 1000));
    sessionStorage.setItem('datos_confirmados', data.datos_confirmados ? '1' : '0');
    // Limpiar localStorage viejo si existía
    ['token','rol','nombre','expira','datos_confirmados'].forEach(k => localStorage.removeItem(k));
    onLogin(data);
  }

  async function handleLogin() {
    setLoading(true);
    setError('');
    try {
      const r = await axios.post(`${API}/api/auth/login`, { email: form.email, password: form.password });
      guardarSesion(r.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  }

  async function verificarDni(dni) {
    if (!dni || dni.length < 6) { setEstadoDni(null); return; }
    setVerificandoDni(true);
    try {
      const r = await axios.get(`${API}/api/auth/verificar-dni?dni=${encodeURIComponent(dni)}`);
      if (!r.data.existe) {
        setEstadoDni('libre');
      } else if (r.data.pre_registrado) {
        setEstadoDni('pre_registrado');
        setNombrePreRegistrado(r.data.nombre || '');
      } else {
        setEstadoDni('ocupado');
      }
    } catch {
      setEstadoDni('libre');
    } finally {
      setVerificandoDni(false);
    }
  }

  async function handleRegistro() {
    setLoading(true);
    setError('');
    try {
      const r = await axios.post(`${API}/api/auth/registro`, form);
      guardarSesion(r.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al registrarse');
    } finally {
      setLoading(false);
    }
  }

  async function handleActivar() {
    if (!form.password) { setError('Ingresá una contraseña.'); return; }
    setLoading(true);
    setError('');
    try {
      const r = await axios.post(`${API}/api/auth/activar`, {
        dni: form.dni,
        email: form.email || undefined,
        password: form.password,
      });
      guardarSesion(r.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al activar la cuenta');
    } finally {
      setLoading(false);
    }
  }

  function resetTab(v) {
    setTab(v);
    setError('');
    setEstadoDni(null);
    setNombrePreRegistrado('');
    setRecuperando(false);
    setExitoRec('');
    setFormRec({ dni: '', email: '', nueva_password: '', confirmar: '' });
    setForm({ nombre: '', apellido: '', dni: '', email: '', password: '', telefono: '' });
  }

  async function handleRecuperar() {
    if (formRec.nueva_password !== formRec.confirmar) { setError('Las contraseñas no coinciden'); return; }
    if (formRec.nueva_password.length < 6) { setError('La contraseña debe tener al menos 6 caracteres'); return; }
    setLoading(true);
    setError('');
    try {
      const r = await axios.post(`${API}/api/auth/recuperar`, {
        dni: formRec.dni,
        email: formRec.email,
        nueva_password: formRec.nueva_password,
      });
      setExitoRec(`¡Listo, ${r.data.nombre}! Tu contraseña fue actualizada. Ya podés ingresar.`);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al recuperar la contraseña');
    } finally {
      setLoading(false);
    }
  }

  const esPreRegistrado = estadoDni === 'pre_registrado';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'var(--c-fondo)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Paper sx={{ bgcolor: 'var(--c-superficie)', p: { xs: 2, sm: 4 }, borderRadius: 3, width: '100%', maxWidth: 400, border: '1px solid var(--c-primario)', mx: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
          {inicio.logo_base64
            ? <img src={inicio.logo_base64} alt="logo" style={{ width: 64, height: 64, objectFit: 'contain', borderRadius: 8, marginBottom: 8 }} />
            : <LocalHospitalIcon sx={{ fontSize: 48, color: 'var(--c-acento)', mb: 1 }} />
          }
          <Typography variant="h5" sx={{ color: '#e0e0e0', fontWeight: 700 }}>
            {inicio.nombre_clinica || 'Sistema Médico'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#b0b0b0', mt: 0.5 }}>
            {inicio.subtitulo || 'Gestión de turnos e historiales'}
          </Typography>
        </Box>

        <Tabs value={tab} onChange={(e, v) => resetTab(v)} centered sx={{
          mb: 3,
          '& .MuiTab-root': { color: '#b0b0b0' },
          '& .Mui-selected': { color: 'var(--c-acento)' },
          '& .MuiTabs-indicator': { bgcolor: 'var(--c-acento)' }
        }}>
          <Tab label="Ingresar" />
          <Tab label="Registrarme" />
        </Tabs>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* ── TAB LOGIN ── */}
          {tab === 0 && !recuperando && (
            <>
              <Box component="form" onSubmit={e => { e.preventDefault(); handleLogin(); }} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField label="Email o DNI" value={form.email} name="username" autoComplete="username"
                onChange={e => setForm({ ...form, email: e.target.value })}
                InputLabelProps={labelStyle} inputProps={{ ...inputStyle, autoComplete: 'username' }} sx={fieldSx} />
              <TextField label="Contraseña" type="password" value={form.password} name="password" autoComplete="current-password"
                onChange={e => setForm({ ...form, password: e.target.value })}
                InputLabelProps={labelStyle} inputProps={{ ...inputStyle, autoComplete: 'current-password' }} sx={fieldSx} />
              <Button type="submit" variant="contained" fullWidth disabled={loading}
                sx={{ bgcolor: 'var(--c-primario)', '&:hover': { bgcolor: 'var(--c-primario)' }, mt: 1, py: 1.2 }}>
                {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Ingresar'}
              </Button>
              </Box>
              <Button variant="text" size="small" onClick={() => { setRecuperando(true); setError(''); setExitoRec(''); }}
                sx={{ color: 'var(--c-acento)', fontSize: 12, mt: -1, textTransform: 'none' }}>
                ¿Olvidaste tu contraseña?
              </Button>
            </>
          )}

          {/* ── RECUPERAR CONTRASEÑA ── */}
          {tab === 0 && recuperando && (
            <>
              <Typography sx={{ color: 'var(--c-acento)', fontWeight: 600, mb: 0.5 }}>Recuperar contraseña</Typography>
              <Typography sx={{ color: '#b0b0b0', fontSize: 13, mb: 1 }}>
                Ingresá tu DNI y el email con el que te registraste para crear una nueva contraseña.
              </Typography>
              {exitoRec ? (
                <>
                  <Alert severity="success">{exitoRec}</Alert>
                  <Button variant="contained" fullWidth onClick={() => { setRecuperando(false); setExitoRec(''); setError(''); }}
                    sx={{ bgcolor: 'var(--c-primario)', '&:hover': { bgcolor: 'var(--c-primario)' }, mt: 1 }}>
                    Ir a Ingresar
                  </Button>
                </>
              ) : (
                <>
                  <TextField label="DNI" value={formRec.dni}
                    onChange={e => setFormRec({ ...formRec, dni: e.target.value })}
                    InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx} />
                  <TextField label="Email" type="email" value={formRec.email}
                    onChange={e => setFormRec({ ...formRec, email: e.target.value })}
                    InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx} />
                  <TextField label="Nueva contraseña" type="password" value={formRec.nueva_password}
                    onChange={e => setFormRec({ ...formRec, nueva_password: e.target.value })}
                    InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx} />
                  <TextField label="Confirmar contraseña" type="password" value={formRec.confirmar}
                    onChange={e => setFormRec({ ...formRec, confirmar: e.target.value })}
                    onKeyPress={e => e.key === 'Enter' && handleRecuperar()}
                    InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx} />
                  <Button variant="contained" fullWidth disabled={loading} onClick={handleRecuperar}
                    sx={{ bgcolor: 'var(--c-primario)', '&:hover': { bgcolor: 'var(--c-primario)' }, mt: 1, py: 1.2 }}>
                    {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Cambiar contraseña'}
                  </Button>
                  <Button variant="text" size="small" onClick={() => { setRecuperando(false); setError(''); }}
                    sx={{ color: '#b0b0b0', fontSize: 12, textTransform: 'none' }}>
                    ← Volver
                  </Button>
                </>
              )}
            </>
          )}

          {/* ── TAB REGISTRO ── */}
          {tab === 1 && (
            <>
              {/* Campo DNI — siempre visible, es el disparador */}
              <Box>
                <TextField label="DNI *" value={form.dni} fullWidth
                  onChange={e => { setForm({ ...form, dni: e.target.value }); setEstadoDni(null); }}
                  onKeyPress={e => e.key === 'Enter' && verificarDni(form.dni)}
                  InputLabelProps={labelStyle}
                  inputProps={{ ...inputStyle, maxLength: 20 }}
                  sx={fieldSx}
                  helperText="Ingresá tu DNI y presioná Continuar"
                  FormHelperTextProps={{ style: { color: '#b0b0b0' } }}
                />
                {verificandoDni && (
                  <Typography sx={{ fontSize: 12, color: 'var(--c-acento)', mt: 0.5 }}>Verificando...</Typography>
                )}
                {esPreRegistrado && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    <HowToRegIcon sx={{ fontSize: 15, color: '#66bb6a' }} />
                    <Typography sx={{ fontSize: 12, color: '#66bb6a' }}>
                      ¡Hola {nombrePreRegistrado}! Ya tenemos tus datos. Solo elegí una contraseña.
                    </Typography>
                  </Box>
                )}
                {estadoDni === 'ocupado' && (
                  <Typography sx={{ fontSize: 12, color: '#f44336', mt: 0.5 }}>
                    Este DNI ya tiene una cuenta activa. Ingresá desde "Ingresar".
                  </Typography>
                )}
              </Box>

              {/* Botón continuar — solo si aún no verificamos */}
              {estadoDni === null && (
                <Button variant="outlined" fullWidth disabled={verificandoDni || form.dni.length < 6}
                  onClick={() => verificarDni(form.dni)}
                  sx={{ color: 'var(--c-acento)', borderColor: 'var(--c-primario)', '&:hover': { borderColor: 'var(--c-acento)' } }}>
                  {verificandoDni ? 'Verificando...' : 'Continuar'}
                </Button>
              )}

              {/* Campos solo para registro nuevo */}
              {estadoDni === 'libre' && (
                <>
                  <Box sx={{ display: 'flex', gap: 1.5, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <TextField label="Nombre *" value={form.nombre} fullWidth
                      onChange={e => setForm({ ...form, nombre: e.target.value })}
                      InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx} />
                    <TextField label="Apellido *" value={form.apellido} fullWidth
                      onChange={e => setForm({ ...form, apellido: e.target.value })}
                      InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx} />
                  </Box>
                  <TextField label="Teléfono" value={form.telefono}
                    onChange={e => setForm({ ...form, telefono: e.target.value })}
                    InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx} />
                </>
              )}

              {/* Email y contraseña — para nuevo registro y para activación */}
              {(estadoDni === 'libre' || esPreRegistrado) && (
                <>
                  <TextField label={esPreRegistrado ? 'Email (opcional)' : 'Email *'} type="email" value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })}
                    InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx}
                    helperText={esPreRegistrado ? 'Podés dejarlo en blanco si ya lo tenemos' : ''}
                    FormHelperTextProps={{ style: { color: '#b0b0b0' } }}
                  />
                  <TextField label="Contraseña *" type="password" value={form.password}
                    onChange={e => setForm({ ...form, password: e.target.value })}
                    onKeyPress={e => e.key === 'Enter' && (esPreRegistrado ? handleActivar() : handleRegistro())}
                    InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx} />
                  <Button variant="contained" fullWidth disabled={loading}
                    onClick={esPreRegistrado ? handleActivar : handleRegistro}
                    sx={{ bgcolor: 'var(--c-primario)', '&:hover': { bgcolor: 'var(--c-primario)' }, mt: 1, py: 1.2 }}>
                    {loading
                      ? <CircularProgress size={22} sx={{ color: '#fff' }} />
                      : esPreRegistrado ? 'Activar mi cuenta' : 'Crear cuenta'}
                  </Button>
                </>
              )}
            </>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
